import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  getToken,
  getRole,
  getTenantName,
  getCanManageTenants,
  clearToken,
  loginWithGoogle as requestGoogleLogin,
  Role,
} from '../data/authClient';

interface AuthContextValue {
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSuperadmin: boolean;
  canManageTenants: boolean;
  tenantName: string | null;
  loginWithGoogle: (credential: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setTokenState] = useState<string | null>(() => getToken());
  const [role, setRoleState] = useState<Role | null>(() => getRole());
  const [tenantName, setTenantNameState] = useState<string | null>(() => getTenantName());
  const [canManageTenantsFlag, setCanManageTenantsFlag] = useState<boolean>(() => getCanManageTenants());

  const loginWithGoogle = useCallback(async (credential: string) => {
    const {
      token: newToken,
      role: newRole,
      tenantName: newTenantName,
      isSuperadmin: newCanManageTenants,
    } = await requestGoogleLogin(credential);
    setTokenState(newToken);
    setRoleState(newRole);
    setTenantNameState(newTenantName ?? null);
    setCanManageTenantsFlag(newCanManageTenants ?? false);
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setTokenState(null);
    setRoleState(null);
    setTenantNameState(null);
    setCanManageTenantsFlag(false);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: token !== null,
      isAdmin: role === 'admin',
      isSuperadmin: role === 'superadmin',
      canManageTenants: role === 'superadmin' || canManageTenantsFlag,
      tenantName,
      loginWithGoogle,
      logout,
    }),
    [token, role, canManageTenantsFlag, tenantName, loginWithGoogle, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
